import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Button,
    IconButton,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowForward } from "@mui/icons-material";
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SummarizeIcon from '@mui/icons-material/Summarize';
import DeleteIcon from '@mui/icons-material/Delete';
import { getMeasurementMatrix, deleteMeasurement } from "../services/BodyMeasurementsService";
import { useMessageContext } from "../contexts/MessageContext";
import { RTL } from "../cache/CacheRtl";
import Loader from "../componenets/Loader";
import AddBodyMeasurementModal from "../componenets/AddBodyMeasurementModal";
import EditMeasurementModal from "../componenets/EditMeasurementModal";
import { exportAllBodyMeasurementsToPdf } from "../utils/bodyMeasurementsPdfExport";
import { unitLabel } from "../componenets/measurements/measurementUnits";
import { parseMediaDate } from "../utils/progressMediaDate";

// measurementDate is a CALENDAR DAY, so parse it with no timezone involvement.
// `new Date("2026-07-15")` is spec'd to parse a date-only string as UTC MIDNIGHT, and the local
// getDate()/getDay() below then read it in the viewer's zone — west of UTC that rendered the
// previous day and the wrong Hebrew weekday. parseMediaDate builds a LOCAL date and also handles
// the Jackson [y,m,d] array shape, which `new Date(...)` turned into "NaN/NaN/NaN".
export const formatFullDate = (dateString) => {
    const date = parseMediaDate(dateString);
    if (!date) return '—';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

export const getDayName = (dateString) => {
    const date = parseMediaDate(dateString);
    if (!date) return '';
    const days = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
    return days[date.getDay()];
};

const headCellSx = {
    fontWeight: 600,
    color: '#64748B',
    backgroundColor: '#F8FAFC',
    fontSize: '13px',
    borderBottom: '2px solid #E2E8F0',
    whiteSpace: 'nowrap',
    padding: '12px 16px'
};

const bodyCellSx = {
    padding: '10px 16px',
    fontSize: '13px',
    borderBottom: '1px solid #F1F5F9',
    whiteSpace: 'nowrap'
};

// --- Cell with value + change ---
const MeasurementCellContent = ({ cell, unit }) => {
    // Treat a missing cell OR a cell with no numeric value the same — a value-less cell must not
    // crash the whole table via `.toFixed` on null/undefined.
    if (!cell || cell.value == null) {
        return <Typography sx={{ color: '#CBD5E1', fontSize: '13px', textAlign: 'center' }}>-</Typography>;
    }

    const change = cell.change;

    return (
        <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1E293B' }}>
                {cell.value.toFixed(1)} {unit}
            </Typography>
            {change != null && change !== 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.3, mt: 0.3 }}>
                    {change < 0 ? (
                        <TrendingDownIcon sx={{ fontSize: 14, color: '#14B8A6' }} />
                    ) : (
                        <TrendingUpIcon sx={{ fontSize: 14, color: '#EF4444' }} />
                    )}
                    <Typography sx={{
                        fontSize: '12px',
                        fontWeight: 500,
                        color: change < 0 ? '#14B8A6' : '#EF4444'
                    }}>
                        {change > 0 ? `+${change.toFixed(1)}` : change.toFixed(1)}
                    </Typography>
                </Box>
            ) : change === 0 ? (
                <Typography sx={{ fontSize: '11px', color: '#94A3B8', mt: 0.3 }}>—</Typography>
            ) : null}
        </Box>
    );
};

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const BodyMeasurementsTablePage = () => {
    const navigate = useNavigate();
    const params = useParams();
    const { userId } = params;

    const [matrixData, setMatrixData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingCell, setEditingCell] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const { notificationHandler } = useMessageContext();

    useEffect(() => {
        if (userId) {
            fetchMatrix();
        }
    }, [userId]);

    const fetchMatrix = async () => {
        setLoading(true);
        try {
            const response = await getMeasurementMatrix(userId);
            setMatrixData(response.data);
        } catch (err) {
            console.error('Error fetching measurement matrix:', err);
            notificationHandler.error('שגיאה בטעינת הנתונים');
            setMatrixData(null);
        } finally {
            setLoading(false);
        }
    };

    const columns = useMemo(() => {
        if (!matrixData?.columns) return [];
        // Definition-based: server sends name (Hebrew) + unit + active per column.
        // Hide columns whose definition was soft-deleted; order by displayOrder.
        return matrixData.columns
            .filter(col => col.active !== false)
            .map(col => ({
                ...col,
                displayName: col.name,
                unit: unitLabel(col.unit)
            }))
            .sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
    }, [matrixData]);

    // Paginate rows (dates) client-side
    const paginatedRows = useMemo(() => {
        if (!matrixData?.rows) return [];
        const start = page * rowsPerPage;
        return matrixData.rows.slice(start, start + rowsPerPage);
    }, [matrixData, page, rowsPerPage]);

    // Sum of all trends (changes) per measurement type across ALL rows
    const trendSums = useMemo(() => {
        if (!matrixData?.rows || !columns.length) return {};
        const sums = {};
        columns.forEach(col => { sums[col.key] = 0; });
        matrixData.rows.forEach(row => {
            columns.forEach(col => {
                const cell = row.values[col.key];
                if (cell?.change != null) {
                    sums[col.key] += cell.change;
                }
            });
        });
        return sums;
    }, [matrixData, columns]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDownloadAllPdf = async () => {
        setLoading(true);
        try {
            await exportAllBodyMeasurementsToPdf(userId);
            notificationHandler.success('הקובץ הורד בהצלחה');
        } catch (error) {
            console.error('Error generating PDF:', error);
            notificationHandler.error('שגיאה ביצירת הקובץ');
        } finally {
            setLoading(false);
        }
    };

    const handleCellClick = (cell, col, measurementDate) => {
        if (!cell || !cell.id) return;
        setEditingCell({ id: cell.id, value: cell.value, definitionName: col.displayName, unit: col.unit, measurementDate });
        setEditModalOpen(true);
    };

    const handleDeleteRow = async (row) => {
        if (!window.confirm(`למחוק את כל המדידות מתאריך ${formatFullDate(row.measurementDate)}?`)) return;
        setLoading(true);
        try {
            const ids = Object.values(row.values).filter(c => c && c.id).map(c => c.id);
            await Promise.all(ids.map(id => deleteMeasurement(id)));
            notificationHandler.success('המדידות נמחקו בהצלחה');
            await fetchMatrix();
        } catch (err) {
            console.error('Error deleting row:', err);
            notificationHandler.error('שגיאה במחיקת מדידות');
        } finally {
            setLoading(false);
        }
    };

    const totalRows = matrixData?.totalRows || 0;
    const hasData = totalRows > 0;

    return (
        <RTL>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Loader loading={loading} />

            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, px: 3, pt: 2, mb: 2 }}>
                <IconButton
                    onClick={() => navigate(`/app/clients/${userId}`)}
                    sx={{
                        color: "#64748B",
                        "&:hover": {
                            backgroundColor: "#F1F5F9",
                            color: "#1E293B"
                        }
                    }}
                >
                    <ArrowForward />
                </IconButton>
                <Typography sx={{ fontSize: "24px", fontWeight: 700, color: "#1E293B" }}>
                    היסטוריית מדידות גוף
                </Typography>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ px: 3, mb: 3, display: "flex", gap: 2, alignItems: "center" }}>
                <Button
                    variant="contained"
                    onClick={() => setModalOpen(true)}
                    sx={{
                        backgroundColor: "#14B8A6",
                        color: "white",
                        textTransform: "none",
                        borderRadius: "8px",
                        padding: "8px 20px",
                        fontWeight: 500,
                        fontSize: "14px",
                        boxShadow: "none",
                        '&:hover': {
                            backgroundColor: "#0D9488",
                            boxShadow: "none"
                        }
                    }}
                >
                    הוספת מדידה
                </Button>
                <Button
                    variant="outlined"
                    onClick={handleDownloadAllPdf}
                    startIcon={<SummarizeIcon />}
                    disabled={!hasData}
                    sx={{
                        borderColor: "#6366F1",
                        color: "#6366F1",
                        textTransform: "none",
                        borderRadius: "8px",
                        padding: "8px 20px",
                        fontWeight: 500,
                        fontSize: "14px",
                        '&:hover': {
                            borderColor: "#4F46E5",
                            backgroundColor: "#EEF2FF",
                            color: "#4F46E5"
                        },
                        '&:disabled': {
                            borderColor: "#E2E8F0",
                            color: "#94A3B8"
                        }
                    }}
                >
                    ייצוא כל המדידות
                </Button>
            </Box>

            {/* Table */}
            <Box sx={{ margin: "0 40px 40px" }}>
                {!hasData && !loading ? (
                    <Typography sx={{ textAlign: 'center', color: '#94A3B8', py: 4 }}>
                        אין מדידות זמינות
                    </Typography>
                ) : hasData && (
                    <>
                        <TableContainer sx={{
                            backgroundColor: '#fff',
                            borderRadius: '16px',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            maxHeight: '70vh'
                        }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        {/* Actions */}
                                        <TableCell align="center" sx={{ ...headCellSx, minWidth: '50px' }}>
                                        </TableCell>
                                        {/* Row number */}
                                        <TableCell align="center" sx={{ ...headCellSx, minWidth: '50px' }}>
                                            #
                                        </TableCell>
                                        {/* Date */}
                                        <TableCell align="center" sx={{ ...headCellSx, minWidth: '120px' }}>
                                            תאריך מדידה
                                        </TableCell>
                                        {/* Measurement type columns */}
                                        {columns.map(col => (
                                            <TableCell key={col.key} align="center" sx={{ ...headCellSx, minWidth: '110px' }}>
                                                <Box>
                                                    {col.displayName}
                                                </Box>
                                                <Typography sx={{ fontSize: '11px', color: '#94A3B8', fontWeight: 400 }}>
                                                    ({col.unit})
                                                </Typography>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedRows.map((row) => (
                                        <TableRow
                                            key={row.measurementDate}
                                            sx={{ '&:hover': { backgroundColor: '#F8FAFC' } }}
                                        >
                                            {/* Actions */}
                                            <TableCell align="center" sx={bodyCellSx}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDeleteRow(row)}
                                                    sx={{
                                                        color: '#CBD5E1',
                                                        '&:hover': { color: '#EF4444', backgroundColor: '#FEF2F2' }
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                            {/* Row number */}
                                            <TableCell align="center" sx={{
                                                ...bodyCellSx,
                                                color: '#14B8A6',
                                                fontWeight: 600
                                            }}>
                                                #{row.rowNumber}
                                            </TableCell>
                                            {/* Date */}
                                            <TableCell align="center" sx={bodyCellSx}>
                                                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1E293B' }}>
                                                    {formatFullDate(row.measurementDate)}
                                                </Typography>
                                                <Typography sx={{ fontSize: '12px', color: '#94A3B8' }}>
                                                    {getDayName(row.measurementDate)}
                                                </Typography>
                                            </TableCell>
                                            {/* Values */}
                                            {columns.map(col => (
                                                <TableCell
                                                    key={col.key}
                                                    align="center"
                                                    sx={{
                                                        ...bodyCellSx,
                                                        cursor: row.values[col.key]?.id ? 'pointer' : 'default',
                                                        '&:hover': row.values[col.key]?.id ? { backgroundColor: '#F0FDFA' } : {}
                                                    }}
                                                    onClick={() => handleCellClick(
                                                        row.values[col.key],
                                                        col,
                                                        row.measurementDate
                                                    )}
                                                >
                                                    <MeasurementCellContent
                                                        cell={row.values[col.key]}
                                                        unit={col.unit}
                                                    />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                    {/* Trend totals row */}
                                    <TableRow sx={{ backgroundColor: '#F8FAFC' }}>
                                        <TableCell sx={{
                                            ...bodyCellSx,
                                            borderTop: '2px solid #E2E8F0'
                                        }} />
                                        <TableCell align="center" sx={{
                                            ...bodyCellSx,
                                            fontWeight: 700,
                                            color: '#64748B',
                                            borderTop: '2px solid #E2E8F0'
                                        }}>
                                        </TableCell>
                                        <TableCell align="center" sx={{
                                            ...bodyCellSx,
                                            fontWeight: 700,
                                            color: '#64748B',
                                            borderTop: '2px solid #E2E8F0'
                                        }}>
                                            סה״כ שינוי
                                        </TableCell>
                                        {columns.map(col => {
                                            const sum = trendSums[col.key] || 0;
                                            const rounded = parseFloat(sum.toFixed(1));
                                            return (
                                                <TableCell key={col.key} align="center" sx={{
                                                    ...bodyCellSx,
                                                    borderTop: '2px solid #E2E8F0'
                                                }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                        {rounded !== 0 && (
                                                            rounded < 0 ? (
                                                                <TrendingDownIcon sx={{ fontSize: 16, color: '#14B8A6' }} />
                                                            ) : (
                                                                <TrendingUpIcon sx={{ fontSize: 16, color: '#EF4444' }} />
                                                            )
                                                        )}
                                                        <Typography sx={{
                                                            fontSize: '14px',
                                                            fontWeight: 700,
                                                            color: rounded === 0 ? '#94A3B8' : rounded < 0 ? '#14B8A6' : '#EF4444'
                                                        }}>
                                                            {rounded === 0 ? '—' : `${rounded > 0 ? '+' : ''}${rounded}`} {rounded !== 0 ? col.unit : ''}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Pagination */}
                        <TablePagination
                            component="div"
                            count={totalRows}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                            labelRowsPerPage="שורות לעמוד:"
                            labelDisplayedRows={({ from, to, count }) => `${from}-${to} מתוך ${count}`}
                            sx={{
                                mt: 1,
                                '& .MuiTablePagination-toolbar': {
                                    flexDirection: 'row-reverse'
                                }
                            }}
                        />

                        {/* Footer */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mt: 2,
                            pt: 2,
                            borderTop: '1px solid #E2E8F0'
                        }}>
                            <Typography sx={{ color: '#64748B', fontSize: '14px' }}>
                                סה״כ מדידות:
                            </Typography>
                            <Typography sx={{ color: '#1E293B', fontSize: '14px', fontWeight: 600 }}>
                                {totalRows}
                            </Typography>
                        </Box>
                    </>
                )}
            </Box>

            <AddBodyMeasurementModal
                clientId={userId}
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onSave={fetchMatrix}
            />

            <EditMeasurementModal
                open={editModalOpen}
                onClose={() => { setEditModalOpen(false); setEditingCell(null); }}
                cell={editingCell}
                onSave={fetchMatrix}
                notificationHandler={notificationHandler}
            />
        </Box>
        </RTL>
    );
};

export default BodyMeasurementsTablePage;
